/**
 * ALTIORA · InteractiveDemo — Lecteur de scénarios pour la landing.
 *
 * Le visiteur clique sur un scénario (Opportunité / Recommandation /
 * Comparaison / Risque / Priorisation) et voit une reconstitution
 * animée du raisonnement de la plateforme.
 *
 * Aucun appel API — tout est mock scripté pour la démo. La valeur
 * pédagogique est de faire ressentir « ALTIORA en train de penser ».
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radar, Sparkles, GitBranch, ShieldAlert, ListChecks,
  ArrowRight, CheckCircle2, ChevronRight,
} from "lucide-react";

const NAVY = "#0B2545";
const NAVY_DEEP = "#061528";
const CHAMPAGNE = "#C5A880";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const SLATE = "#64748B";
const GREEN = "#0B7A3B";
const AMBER = "#B45309";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 0.68, 0.24, 1] },
};

const SCENARIOS = [
  {
    key: "opportunity",
    icon: Radar,
    label: "Opportunité détectée",
    trigger: "Nouvelle immatriculation · REQ · Cabinet Nadeau Fiscalité",
    steps: [
      { l: "SIGNAL", text: "Immatriculation détectée dans le REQ — 3 associés, siège à Lévis." },
      { l: "ENRICHISSEMENT", text: "Croisement avec LinkedIn + historique fiscal — cible haute valeur." },
      { l: "ANALYSE", text: "Score d'affinité : 82 / 100. Aucun conseiller existant identifié." },
      { l: "DÉCISION", text: "Recommandation : approche personnalisée sous 48 h." },
    ],
    verdict: {
      title: "Contacter Cabinet Nadeau Fiscalité",
      hint: "Fenêtre optimale · 48 h · impact estimé + 3.4 pts",
      color: GOLD,
    },
  },
  {
    key: "recommendation",
    icon: Sparkles,
    label: "Recommandation générée",
    trigger: "Vous avez 25 minutes libres entre 14h et 14h25.",
    steps: [
      { l: "CONTEXTE", text: "Créneau court détecté dans votre calendrier." },
      { l: "PIPELINE", text: "3 tâches à haute valeur peuvent être avancées en < 30 min." },
      { l: "PRIORISATION", text: "Dossier Bergeron — proposition à finaliser (deadline 36 h)." },
      { l: "GESTE", text: "Rédiger + envoyer la proposition. Modèle prêt en 3 clics." },
    ],
    verdict: {
      title: "Envoyer la proposition Bergeron",
      hint: "25 min suffisent · deadline critique · impact + 1.8 pt",
      color: CHAMPAGNE,
    },
  },
  {
    key: "compare",
    icon: GitBranch,
    label: "Comparer deux décisions",
    trigger: "Faut-il appeler Cabinet Dubois aujourd'hui ou dans une semaine ?",
    steps: [
      { l: "SCÉNARIO A", text: "Attendre 7 jours — probabilité de conversion : 41 %." },
      { l: "SCÉNARIO B", text: "Appeler aujourd'hui — probabilité de conversion : 68 %." },
      { l: "PROJECTION", text: "Delta net : + 27 pts de probabilité en agissant sous 24 h." },
      { l: "COÛT", text: "Coût d'opportunité de l'attente : 3.4 pts de trajectoire." },
    ],
    verdict: {
      title: "Agir dans les 24 h",
      hint: "Scénario B · gain net + 4.8 pts vs. attendre",
      color: GOLD,
    },
  },
  {
    key: "risk",
    icon: ShieldAlert,
    label: "Analyse de risque",
    trigger: "2 dossiers sans contact depuis plus de 45 jours.",
    steps: [
      { l: "DÉTECTION", text: "Silence radio détecté — dossiers Létourneau + Beaudry." },
      { l: "HISTORIQUE", text: "Cycle habituel du client · 21 jours. Écart significatif." },
      { l: "EXPOSITION", text: "Valeur combinée à risque : 340 k. Probabilité fuite : 34 %." },
      { l: "MITIGATION", text: "Deux relances ciblées suffisent à rétablir le lien." },
    ],
    verdict: {
      title: "Relancer les 2 dossiers cette semaine",
      hint: "Réduction du risque · 34 % → 8 %",
      color: AMBER,
    },
  },
  {
    key: "priority",
    icon: ListChecks,
    label: "Priorisation automatique",
    trigger: "12 tâches en file d'attente pour aujourd'hui.",
    steps: [
      { l: "TRI", text: "Classement par ROI × urgence × probabilité de conversion." },
      { l: "ARBITRAGE", text: "3 tâches génèrent 68 % de la valeur totale de la journée." },
      { l: "FOCUS", text: "Les 9 autres sont différées, déléguées ou automatisées." },
      { l: "PLANIFICATION", text: "Créneaux calendaires proposés selon vos heures de pic." },
    ],
    verdict: {
      title: "Focus sur 3 gestes · reporter 9",
      hint: "Gain net estimé + 7.3 pts · effort divisé par 4",
      color: GOLD,
    },
  },
];


function StepRow({ step, i, active }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: active ? 1 : 0.35, x: 0 }}
      transition={{ delay: i * 0.35, duration: 0.55, ease: [0.16, 0.68, 0.24, 1] }}
      className="flex items-start gap-4 py-3"
      data-testid={`demo-step-${i}`}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.24em] pt-1 min-w-[110px]"
           style={{ color: CHAMPAGNE }}>
        {step.l}
      </div>
      <div className="flex-1 text-[13.5px] leading-relaxed" style={{ color: `${IVORY}DD` }}>
        {step.text}
      </div>
    </motion.div>
  );
}


export default function InteractiveDemo() {
  const [activeKey, setActiveKey] = useState("opportunity");
  const active = useMemo(
    () => SCENARIOS.find((s) => s.key === activeKey) || SCENARIOS[0],
    [activeKey],
  );

  // Petit auto-cycle pour animer l'apparition des étapes (à chaque switch)
  const [renderTick, setRenderTick] = useState(0);
  useEffect(() => {
    setRenderTick((t) => t + 1);
  }, [activeKey]);

  return (
    <section
      id="section-interactive-demo"
      className="relative"
      style={{ background: NAVY_DEEP, color: IVORY }}
      data-testid="section-interactive-demo"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 30% 30%, ${GOLD}, transparent 60%)` }}
      />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32 relative">
        <motion.div {...fadeUp}>
          <div className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.32em]"
               style={{ color: GOLD }}>
            <span className="h-px w-6" style={{ background: GOLD }} />
            Démonstration interactive
          </div>
        </motion.div>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 font-serif leading-[1.1] tracking-tight max-w-3xl"
          style={{ color: IVORY, fontSize: "clamp(1.9rem, 4vw, 2.8rem)", fontWeight: 400 }}
          data-testid="section-interactive-demo-title"
        >
          Choisissez un scénario.
          <br />
          <span style={{ fontStyle: "italic", color: GOLD }}>
            Regardez ALTIORA raisonner.
          </span>
        </motion.h2>

        {/* Onglets scénarios */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-12 flex flex-wrap gap-2"
          data-testid="demo-tabs"
        >
          {SCENARIOS.map((s) => {
            const on = s.key === activeKey;
            return (
              <button
                key={s.key}
                onClick={() => setActiveKey(s.key)}
                data-testid={`demo-tab-${s.key}`}
                aria-pressed={on}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] transition-all"
                style={{
                  background: on ? IVORY : `${IVORY}0A`,
                  color: on ? NAVY_DEEP : IVORY,
                  border: `1px solid ${on ? IVORY : `${CHAMPAGNE}33`}`,
                  fontWeight: on ? 600 : 400,
                }}
              >
                <s.icon size={13} strokeWidth={1.8} />
                {s.label}
              </button>
            );
          })}
        </motion.div>

        {/* Panneau scénario */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.25 }}
          className="mt-8 rounded-xl overflow-hidden"
          style={{ background: `${IVORY}08`, border: `1px solid ${CHAMPAGNE}22` }}
          data-testid="demo-panel"
        >
          {/* Bandeau trigger */}
          <div className="px-6 py-4 flex items-center gap-3"
               style={{ background: `${IVORY}05`, borderBottom: `1px solid ${CHAMPAGNE}18` }}>
            <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                 style={{ background: `${GOLD}22`, color: GOLD }}>
              <active.icon size={16} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
                Signal entrant
              </div>
              <div className="text-[14px] leading-tight" style={{ color: IVORY }}>
                {active.trigger}
              </div>
            </div>
          </div>

          {/* Steps animés */}
          <div className="px-6 py-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${active.key}-${renderTick}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {active.steps.map((step, i) => (
                  <StepRow key={i} step={step} i={i} active={true} />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Verdict */}
          <motion.div
            key={`verdict-${active.key}-${renderTick}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: active.steps.length * 0.35 + 0.1, duration: 0.6 }}
            className="px-6 py-5 flex items-center gap-4"
            style={{ background: `${active.verdict.color}0F`, borderTop: `1px solid ${active.verdict.color}44` }}
            data-testid="demo-verdict"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                 style={{ background: active.verdict.color, color: NAVY_DEEP }}>
              <CheckCircle2 size={16} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: active.verdict.color }}>
                Verdict ALTIORA
              </div>
              <div className="text-[15px] md:text-[16px] leading-tight"
                   style={{ color: IVORY, fontWeight: 500 }}>
                {active.verdict.title}
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: `${IVORY}99` }}>
                {active.verdict.hint}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: `${IVORY}66` }} />
          </motion.div>
        </motion.div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.35 }}
          className="mt-8 max-w-2xl text-[13px]"
          style={{ color: `${IVORY}88` }}
        >
          Chaque scénario est reconstitué à partir de raisonnements
          réels de la plateforme. En production, ALTIORA traite ces
          arbitrages en continu, silencieusement, dans le fond.
        </motion.p>
      </div>
    </section>
  );
}
